/// <amd-module name="Analytics/Boards/BurndownQueries/CurrentEffortQuery" />
import { FieldFilter, WorkItemFieldDescriptor } from "Analytics/Boards/BoardsQueries/DataContracts";
import { AggregationMode, ModefulValueSetting, TeamScope } from "Analytics/Boards/BoardsQueries/DataContracts";
import { WitFieldUtilities } from "Analytics/Boards/BoardsQueries/WitFieldUtilities";
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as PublicProjectsQueryHelper from "Analytics/Common/Utilities/PublicProjectsQueryHelper";
import { QueryUtilities } from "Analytics/Common/Utilities/QueryUtilities";
import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";
import { IVssPageContext } from "VSS/Platform/Context";
import { uniqueSort } from "VSS/Platform/Util/Array";
import { CurrentWorkItemAggregateEffort } from "./BurndownDataContract";
import { BurndownQueryBase } from "./BurndownQueryBase";

/**
 * This calculates remaining effort by querying every item that has remaining effort in the effort field
 * This queries by single work item type configured.
 *
 * This is used in burndown only for the completed % submetric, total scope change submetric, and hero total effort metric.
 * The main difference between this and EffortSnapshotsQuery is that this query is querying the most recent data (today's)
 * as opposed to EffortSnapshotsQuery which is using the constructed snapshots of work item history at various points of time
 * in the past.
 *
 * Note: the only hard filter here is teams and projects - this is not iteration driven nor is this following dates picked,
 * so users need to apply filters for this number to be useful
 */
export class CurrentEffortQuery extends BurndownQueryBase<CurrentWorkItemAggregateEffort[]> {
    private static generateQueryOptions(
        pageContext: IVssPageContext,
        teamScopes: TeamScope[],
        filters: FieldFilter[],
        aggregation: ModefulValueSetting<AggregationMode, string>,
        workItemTypes: string[],
        workItemTypeDescriptors: WorkItemFieldDescriptor[],
        showResolvedItemsAsCompleted = false
    ): ODataQueryOptions {
        // $apply
        const teams = teamScopes.map(scope => scope.teamId);
        const projects = uniqueSort(teamScopes.map(scope => scope.projectId));

        const teamsClause = QueryUtilities.isInArray("t/TeamSK", teams, false /* useQuotes */);
        const projectsClause = QueryUtilities.isInArray("ProjectSK", projects, false /* useQuotes */);
        const projectsTagClause = QueryUtilities.isInArray("t/ProjectSK", projects, false /* useQuotes */);

        const workItemTypesClause = QueryUtilities.isInArray("WorkItemType", workItemTypes, true /* useQuotes */);

        let $apply =
            `filter(` + `(${projectsClause})` + ` and Teams/any(t:${teamsClause})` + ` and (${workItemTypesClause})` + ` and StateCategory ne null`;

        const filtersClause = WitFieldUtilities.getFiltersClause(filters, workItemTypeDescriptors);
        if (filtersClause) {
            if(filtersClause.includes('Tags/any(t:t/TagName'))
            {
                const removeLastChar = filtersClause.slice(0, -1);              
                $apply += ` and (${removeLastChar} and ${projectsTagClause}))`;
            }
            else
            {
                $apply += ` and (${filtersClause})`;
            }
        }

        $apply += ")";

        let aggregateFunction = "aggregate($count as AggregatedEffort)";
        if (aggregation.identifier === AggregationMode.Sum) {
            const analyticsEffortFieldName = WitFieldUtilities.getFieldODataPropertyName(aggregation.settings);
            aggregateFunction = `aggregate(${analyticsEffortFieldName} with sum as AggregatedEffort)`;
        }

        if(showResolvedItemsAsCompleted){
            $apply += `/groupby((StateCategory, State),${aggregateFunction})`;
        }
        else{
            $apply += `/groupby((StateCategory),${aggregateFunction})`;
        }
        

        return PublicProjectsQueryHelper.forceProjectScoping(pageContext, projects, {
            entityType: "WorkItems",
            oDataVersion: latestODataVersion,
            $apply: $apply
        });
    }
    constructor(
        pageContext: IVssPageContext,
        teamScopes: TeamScope[],
        filters: FieldFilter[],
        aggregation: ModefulValueSetting<AggregationMode, string>,
        workItemTypes: string[],
        workItemTypeDescriptors: WorkItemFieldDescriptor[],
        showResolvedItemsAsCompleted = false,
        commandName?: string
    ) {
        super(
            CurrentEffortQuery.generateQueryOptions(pageContext, teamScopes, filters, aggregation, workItemTypes, workItemTypeDescriptors, showResolvedItemsAsCompleted),
            commandName
        );
    }

    public getQueryName(): string {
        return "CurrentEffortQuery";
    }

    protected interpretQueryResults(data: { value: CurrentWorkItemAggregateEffort[] }): CurrentWorkItemAggregateEffort[] {
        // This null filter is only needed because we don't include a 'ne null' filter for effort in the query.
        // As of this writing the reason we don't include it in the query is to get better SQL perf.
        // That may or may not change in the future.
        return data.value.filter(value => value.AggregatedEffort != null);
    }
}
